/**
 * CR324: Preview Diagnostics Panel
 *
 * Collapsible debug panel (behind ?debug=1 or always visible when showDebug=true)
 * showing the render signature hash, applied layers, warnings, and a
 * "Copy Diagnostics" button for fast triage.
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, Copy, Check, Hash } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PreviewSourceMap } from "@/lib/deals/preview/assembleDealCardPreview";

interface PreviewDiagnosticsPanelProps {
  renderSignatureHash: string;
  sourceMap: PreviewSourceMap;
  diagnosticsJson: string;
  className?: string;
}

export function PreviewDiagnosticsPanel({
  renderSignatureHash,
  sourceMap,
  diagnosticsJson,
  className,
}: PreviewDiagnosticsPanelProps) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(diagnosticsJson);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      console.log("[CR324] Diagnostics:", diagnosticsJson);
    }
  };

  const { appliedLayers, warnings } = sourceMap.summary;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={className}>
      <div className="flex items-center gap-2 text-[10px] font-mono bg-slate-900 text-slate-300 px-3 py-1.5 rounded-t">
        {/* Hash */}
        <Hash className="h-3 w-3 text-slate-500" />
        <span className="text-cyan-400">{renderSignatureHash.slice(0, 8)}</span>

        {/* Applied layers */}
        <span className="text-slate-500">layers=</span>
        {appliedLayers.map((layer) => (
          <Badge
            key={layer}
            variant="outline"
            className="text-[9px] px-1 py-0 h-4 border-slate-600 text-green-400"
          >
            {layer}
          </Badge>
        ))}

        {/* Warnings count */}
        {warnings.length > 0 && (
          <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 border-amber-600 text-amber-400">
            ⚠ {warnings.length}
          </Badge>
        )}

        <div className="flex-1" />

        {/* Copy button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="h-5 px-1.5 text-[9px] text-slate-400 hover:text-white"
        >
          {copied ? (
            <><Check className="h-3 w-3 mr-1" /> Copied</>
          ) : (
            <><Copy className="h-3 w-3 mr-1" /> Copy Diagnostics</>
          )}
        </Button>

        {/* Expand toggle */}
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-slate-400">
            <ChevronDown className={cn("h-3 w-3 transition-transform", open && "rotate-180")} />
          </Button>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent>
        <div className="text-[10px] font-mono bg-slate-950 text-slate-400 px-3 py-2 rounded-b border-t border-slate-800 space-y-1.5">
          {/* Layer details */}
          <div className="space-y-0.5">
            <span className="text-slate-500">── Input Layers ──</span>
            {Object.entries(sourceMap.inputs).map(([key, meta]) => (
              <div key={key} className="flex gap-2">
                <span className="w-28 text-slate-500">{key}</span>
                <span className={meta.present ? "text-green-400" : "text-slate-600"}>
                  {meta.present ? "✓ present" : "✗ absent"}
                </span>
                <span className={meta.applied ? "text-cyan-400" : "text-slate-600"}>
                  {meta.applied ? "→ applied" : "→ skipped"}
                </span>
                {meta.hash && (
                  <span className="text-purple-400">#{meta.hash.slice(0, 6)}</span>
                )}
              </div>
            ))}
          </div>

          {/* Warnings */}
          {warnings.length > 0 && (
            <div className="space-y-0.5">
              <span className="text-amber-500">── Warnings ──</span>
              {warnings.map((w, i) => (
                <div key={i} className="text-amber-400">⚠ {w}</div>
              ))}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
